// Thin wrappers around the billing Edge Functions. Every paywall call site
// imports from here — never calls fetch() or supabase.functions.invoke()
// directly, so this file is the ONE place to change if the Edge Function
// contracts evolve.
//
// Uses supabase.functions.invoke() to match the house style (see createLead
// in lib/supabaseClient.ts). invoke() attaches the current session's
// access_token to the Authorization header automatically.

import { supabase } from "../lib/supabaseClient";

/**
 * Open a Stripe Checkout session for the given plan and redirect the user
 * to Stripe — OR, if they're already on a subscription, swap the existing
 * Stripe subscription's price in-place (proration applied automatically).
 *
 * The Edge Function picks the right path based on the user's current Stripe
 * state. We return a discriminated outcome so the caller knows which
 * happened:
 *
 *   { redirected: true }     — page is navigating to Stripe Checkout. The
 *                              caller will never see this in practice
 *                              because `window.location.assign` has already
 *                              fired, but it documents the contract.
 *   { swapped: true, plan }  — existing sub had its price swapped to `plan`
 *                              via stripe.subscriptions.update. Caller
 *                              should toast + refresh.
 *   { alreadyOnPlan: true, plan }
 *                            — user is already on this exact plan. Caller
 *                              should show an info toast.
 *
 * Errors still bubble up as thrown Errors. The legacy 409 "already_subscribed"
 * path is preserved for safety (older Edge Function deploys), but in practice
 * the swap path replaces it.
 *
 * @param {"customer_pro" | "customer_pro_yearly" | "artist_pro" | "artist_pro_yearly" | "single_design"} plan
 */
export async function startCheckout(plan) {
  const { data, error } = await supabase.functions.invoke("stripe-checkout", {
    // Pass the current origin so the Edge Function can build Stripe's
    // success/cancel URLs that come back HERE (prod, preview, or dev).
    // The function validates against an allowlist before trusting it.
    body: { plan, appBaseUrl: window.location.origin },
  });

  if (error) {
    // supabase.functions.invoke surfaces 4xx as a FunctionsHttpError. The
    // function's JSON body lives on error.context.response. Try to parse
    // our structured payload out so callers can react to specific errors
    // without string-matching the message.
    let parsed = null;
    try {
      parsed = await error.context?.response?.json?.();
    } catch {
      // body wasn't JSON — fall through
    }
    // Legacy duplicate-sub error from older Edge Function deploys. The new
    // deploy uses the swap path instead, so this should not fire in normal
    // operation — but keep the handler so a stale function doesn't break
    // the client.
    if (parsed?.error === "already_subscribed") {
      const e = new Error(parsed.message || "You already have a subscription.");
      e.code = "already_subscribed";
      e.billingUrl = parsed.billing_url || "/account/billing";
      throw e;
    }
    console.error("[billing] checkout error", error);
    throw new Error(error.message || "Could not start checkout");
  }

  // Plan swap path — Edge Function updated the existing subscription in place.
  if (data?.swapped) {
    return { swapped: true, plan: data.plan ?? plan };
  }
  // Already on this exact plan — no-op.
  if (data?.already_on_plan) {
    return { alreadyOnPlan: true, plan: data.plan ?? plan };
  }
  // New-subscription path — redirect to Stripe.
  if (!data?.url) {
    throw new Error("Checkout returned no URL");
  }

  window.location.assign(data.url);
  return { redirected: true };
}

/**
 * Open the Stripe Billing Portal so users can update card or download
 * invoices. Cancellation is handled by our own button instead — see
 * cancelSubscription() — so we control the UX (no buried cancel link).
 *
 * Redirects in the same tab; users return via the Stripe "Return to Tattune"
 * link (configured in the Stripe dashboard).
 */
export async function openBillingPortal() {
  const { data, error } = await supabase.functions.invoke("stripe-portal", {
    body: { appBaseUrl: window.location.origin },
  });

  if (error) {
    console.error("[billing] portal error", error);
    throw new Error(error.message || "Could not open billing portal");
  }
  if (!data?.url) {
    throw new Error("Portal returned no URL");
  }

  window.location.assign(data.url);
}

/**
 * Cancel the current user's subscription at the end of the paid period.
 * They keep access until current_period_end. Returns the new state so the
 * caller can update UI optimistically; the webhook will reconcile shortly.
 */
export async function cancelSubscription() {
  const { data, error } = await supabase.functions.invoke(
    "stripe-cancel-subscription",
    { body: { action: "cancel" } }
  );

  if (error) {
    console.error("[billing] cancel error", error);
    throw new Error(error.message || "Could not cancel subscription");
  }
  return data;
}

/**
 * Undo a pending cancellation. Only works while the subscription is still
 * active (i.e. before current_period_end has elapsed and the webhook has
 * flipped status to canceled).
 */
export async function resumeSubscription() {
  const { data, error } = await supabase.functions.invoke(
    "stripe-cancel-subscription",
    { body: { action: "resume" } }
  );

  if (error) {
    console.error("[billing] resume error", error);
    throw new Error(error.message || "Could not resume subscription");
  }
  return data;
}
